/**
 * Delivery options block (custom).
 *
 * Renders the shipping / pickup / local delivery availability of a product, with the earliest
 * date each method can honour, without asking the customer for a postcode first.
 *
 * Data comes from the Zapiet Pickup + Delivery storefront API, which is the source of truth for
 * preparation times, opening days and blackout dates. Two endpoints are used:
 *
 *   POST /v1.0/product-widget/locations  {product}       -> pickup locations, each with a `minDate`
 *   POST /v1.0/shipping/calendar         {shoppingCart}  -> date picker settings for shipping
 *
 * The `/v1.0/product-widget` endpoint used by Zapiet's own product block is deliberately avoided:
 * it returns a 500 when no postcode is supplied, which is exactly the constraint we remove here.
 *
 * Zapiet has no notion of the Wonka "etat" metafield, which drives availability on this shop. The
 * state of each variant is rendered by Liquid in `data-variant-states`; a variant that cannot be
 * bought gets every row marked as unavailable, without asking Zapiet anything.
 *
 * Pickup only products (`data-pickup-only`, from a product tag) get the same treatment for shipping
 * and local delivery: Zapiet refuses them at checkout, but its calendars do not reflect it.
 *
 * Pre-order products pass their delivery date in `data-min-date` (YYYY-MM-DD). It acts as a floor on the
 * date each available row announces, since Zapiet's calendars know nothing about it.
 *
 * Home delivery rows may carry an info bubble explaining why the product cannot be delivered. It opens
 * on hover through CSS, and on tap / keyboard through `aria-expanded`, since a tap gives no hover and
 * Safari does not focus a tapped button.
 *
 * The block degrades quietly: if the API is unreachable or its payload changes shape, the rows keep
 * their titles and simply drop the availability line rather than showing a wrong promise.
 */

const API_BASE = 'https://api.zapiet.com/v1.0';
const CACHE_PREFIX = 'custom-delivery-options';
const CACHE_TTL = 10 * 60 * 1000;
const REQUEST_TIMEOUT = 6000;
const MAX_LOOKAHEAD_DAYS = 120;
const ZAPIET_READY_TIMEOUT = 5000;

const pad = (value) => String(value).padStart(2, '0');

/**
 * Today, as YYYY-MM-DD, in the location's own timezone. A Zurich store is a day ahead of a customer
 * browsing from Vancouver, and the dates Zapiet returns are always local to the location.
 */
function todayIn(timezone) {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };

  try {
    return new Intl.DateTimeFormat('en-CA', { ...options, timeZone: timezone }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('en-CA', options).format(new Date());
  }
}

/**
 * Zapiet mixes two date shapes in the same payload: ISO-ish strings ("2026-8-1") for the calendar
 * boundaries, and [year, monthIndex, day] tuples inside the `disabled` array, where the month is
 * zero-based because that array is passed straight through to a pickadate configuration.
 */
function toIsoDate(value) {
  if (Array.isArray(value) && value.length >= 3) {
    return `${value[0]}-${pad(Number(value[1]) + 1)}-${pad(value[2])}`;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parts = value.trim().split(/[-/]/).map(Number);

    if (parts.length >= 3 && parts.every(Number.isFinite)) {
      return `${parts[0]}-${pad(parts[1])}-${pad(parts[2])}`;
    }
  }

  return null;
}

function addDays(isoDate, days) {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  date.setUTCDate(date.getUTCDate() + days);

  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** Day of the week using the pickadate convention Zapiet relies on: 1 = Sunday … 7 = Saturday. */
function weekdayIndex(isoDate) {
  const [year, month, day] = isoDate.split('-').map(Number);

  return new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 1;
}

/**
 * Resolves the first date a calendar can actually honour, mirroring the rules Zapiet applies in its
 * own cart widget: a minimum date from the preparation time, closed weekdays, blackout dates, and an
 * optional allow list that takes over the whole calendar (used for fixed-date products such as a
 * Valentine's Day cake).
 *
 * @param {object} settings - the payload returned by the calendar endpoint
 * @param {Array} [extraDisabled] - dates closed for another reason, typically daily order limits
 * @returns {string|null} the date as YYYY-MM-DD, or null when nothing is available
 */
function firstAvailableDate(settings, extraDisabled = []) {
  const today = todayIn(settings.timezone);
  const minDate = toIsoDate(settings.minDate);
  const maxDate = toIsoDate(settings.maxDate);
  const start = minDate && minDate > today ? minDate : today;

  const disabled = Array.isArray(settings.disabled) ? settings.disabled : [];
  const blocked = new Set(extraDisabled.map(toIsoDate).filter(Boolean));

  // A leading `true` flips the meaning of the array: the entries that follow become the only dates
  // that are open, instead of the ones that are closed.
  if (disabled[0] === true) {
    const allowed = disabled
      .slice(1)
      .map(toIsoDate)
      .filter((date) => date && !blocked.has(date))
      .sort();

    return allowed.find((date) => date >= start && (!maxDate || date <= maxDate)) ?? null;
  }

  const closedWeekdays = disabled.filter((entry) => typeof entry === 'number');

  disabled.forEach((entry) => {
    const date = Array.isArray(entry) ? toIsoDate(entry) : null;

    if (date) {
      blocked.add(date);
    }
  });

  for (let offset = 0; offset < MAX_LOOKAHEAD_DAYS; offset++) {
    const date = addDays(start, offset);

    if (maxDate && date > maxDate) {
      return null;
    }

    if (closedWeekdays.includes(weekdayIndex(date)) || blocked.has(date)) {
      continue;
    }

    return date;
  }

  return null;
}

function readCache(key) {
  try {
    const entry = JSON.parse(window.sessionStorage.getItem(key));

    return entry && Date.now() - entry.at < CACHE_TTL ? entry.value : null;
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), value }));
  } catch {
    // Private browsing, or a full quota. The block just loses its cache.
  }
}

class DeliveryOptions extends HTMLElement {
  connectedCallback() {
    try {
      this.product = JSON.parse(this.dataset.product);
    } catch {
      return;
    }

    try {
      this.variantStates = JSON.parse(this.dataset.variantStates || '{}');
    } catch {
      this.variantStates = {};
    }

    this.pickupOnly = this.hasAttribute('data-pickup-only');
    this.minDate = toIsoDate(this.dataset.minDate);
    this.shop = this.dataset.shop;
    this.locale = document.documentElement.lang || 'fr';
    this.rows = new Map(
      Array.from(this.querySelectorAll('[data-method]')).map((row) => [row.dataset.method, row])
    );

    this.abortController = new AbortController();

    document.addEventListener('variant:change', this.onVariantChange, {
      signal: this.abortController.signal
    });

    this.querySelector('[data-store-modal]')?.addEventListener('click', this.onStoreModalClick, {
      signal: this.abortController.signal
    });

    this.addEventListener('click', this.onInfoClick, { signal: this.abortController.signal });
    document.addEventListener('click', this.onOutsideClick, { signal: this.abortController.signal });
    document.addEventListener('keydown', this.onKeyDown, { signal: this.abortController.signal });

    this.hideMethodsDisabledInZapiet();
    this.refresh();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  onVariantChange = (event) => {
    const variantId = event.detail?.variant?.id;

    if (!variantId || variantId === this.product.variant_id) {
      return;
    }

    this.product = { ...this.product, variant_id: variantId };
    this.refresh();
  };

  onInfoClick = (event) => {
    const toggle = event.target.closest('.delivery-options__info-toggle');

    if (!toggle) {
      return;
    }

    const expanded = toggle.getAttribute('aria-expanded') === 'true';

    this.closeInfos();
    toggle.setAttribute('aria-expanded', String(!expanded));
  };

  onOutsideClick = (event) => {
    if (!event.target.closest?.('.delivery-options__info')) {
      this.closeInfos();
    }
  };

  onKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.closeInfos();
    }
  };

  closeInfos() {
    this.querySelectorAll('.delivery-options__info-toggle[aria-expanded="true"]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  onStoreModalClick = (event) => {
    // Reuse Zapiet's own store picker rather than rebuilding one: it carries the search, the
    // pagination and the opening hours we would otherwise have to reimplement.
    const picker = window.ZapietProductDeliveryInfo;

    if (!picker?.showModal) {
      return;
    }

    event.preventDefault();

    // `showModal` fetches and renders the store list itself, but only once Zapiet's postcode flow
    // has seeded `delivery_info_data` — a step we skip. Seeding it here is what makes the modal
    // usable straight from this block. It must also be reset on every open: the fetch pushes onto
    // that array, so reopening would otherwise stack the same stores again.
    if (window.ZapietCachedSettings) {
      window.ZapietCachedSettings.delivery_info_data = { locations: [] };
    }

    picker.showModal();
  };

  /**
   * The store picker belongs to Zapiet's own product block, which boots on `window.load` — later
   * than this component. Rather than deciding too early and leaving the link hidden for good, wait
   * for it, and give up only if the block is genuinely absent from the page.
   */
  whenStorePickerReady() {
    this.storePickerReady ??= new Promise((resolve) => {
      const startedAt = Date.now();

      const poll = () => {
        if (window.ZapietProductDeliveryInfo?.showModal) {
          resolve(true);
        } else if (Date.now() - startedAt > ZAPIET_READY_TIMEOUT) {
          resolve(false);
        } else {
          setTimeout(poll, 150);
        }
      };

      poll();
    });

    return this.storePickerReady;
  }

  /**
   * A method turned off in the Zapiet admin can never be picked at checkout, so advertising it on
   * the product page would be a promise the cart cannot keep.
   */
  hideMethodsDisabledInZapiet() {
    const methods = window.ZapietCachedSettings?.cached_config?.methods;

    if (!methods) {
      return;
    }

    this.rows.forEach((row, method) => {
      if (methods[method] && methods[method].enabled === false) {
        row.hidden = true;
        this.rows.delete(method);
      }
    });

    if (this.rows.size === 0) {
      this.hidden = true;
    }
  }

  async request(path, body) {
    const timeout = AbortSignal.timeout?.(REQUEST_TIMEOUT);
    const response = await fetch(`${API_BASE}/${path}?shop=${encodeURIComponent(this.shop)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: timeout
    });

    if (!response.ok) {
      throw new Error(`Zapiet ${path} responded with ${response.status}`);
    }

    return response.json();
  }

  async refresh() {
    const variantId = this.product.variant_id;

    // A variant with no known state (not synced from Wonka) falls through to Zapiet as before.
    const variantState = this.variantStates[variantId];

    if (variantState && variantState !== 'available') {
      // Flagged so the info bubble does not blame shipping for what is simply a sold out variant.
      const unavailable = { state: 'unavailable', soldOut: true };

      this.render({ pickup: unavailable, shipping: unavailable, delivery: unavailable });
      return;
    }
    // The pickup only flag is part of the key, so a product that just got (or lost) the tag does not
    // replay what was cached before.
    const cacheKey = `${CACHE_PREFIX}:${this.shop}:${variantId}${this.pickupOnly ? ':pickup-only' : ''}`;
    const cached = readCache(cacheKey);

    if (cached) {
      this.render(cached);
      return;
    }

    this.rows.forEach((row) => this.setRowState(row, 'loading'));

    const [pickup, shipping, delivery] = await Promise.all([
      this.rows.has('pickup') ? this.fetchPickup() : null,
      this.lookupHomeMethod('shipping'),
      this.lookupHomeMethod('delivery')
    ]);

    const result = { pickup, shipping, delivery };

    writeCache(cacheKey, result);

    // Two variant switches in quick succession can resolve out of order; only the response for the
    // variant still on screen is allowed to paint.
    if (this.product.variant_id === variantId) {
      this.render(result);
    }
  }

  /**
   * Shipping and local delivery both bring the product to the customer, which a pickup only product
   * cannot do whatever their calendars say.
   */
  lookupHomeMethod(method) {
    if (!this.rows.has(method)) {
      return null;
    }

    return this.pickupOnly ? { state: 'unavailable' } : this.fetchCalendar(method);
  }

  /**
   * Pickup availability comes from the location list rather than a calendar: each location already
   * carries the earliest date it can prepare this specific product.
   */
  async fetchPickup() {
    try {
      const data = await this.request('product-widget/locations', {
        product: this.product,
        query: '',
        page: 1
      });

      const locations = (data.locations ?? []).filter((location) => location.available);

      if (locations.length === 0) {
        return { state: 'unavailable' };
      }

      const dates = locations.map(
        (location) => toIsoDate(location.minDate) ?? todayIn(location.timezone)
      );

      return {
        state: 'available',
        date: dates.sort()[0],
        timezone: locations[0].timezone,
        locationName: locations[0].company_name,
        locationCount: locations.length
      };
    } catch (error) {
      console.warn('[delivery-options] pickup lookup failed', error);
      return { state: 'unknown' };
    }
  }

  /**
   * Zapiet only keeps a calendar for a method whose date picker is on. With the picker off the
   * calendar endpoint still answers `DATES_NOT_AVAILABLE`, which means "there is no calendar", not
   * "the method is closed" — reading it as unavailable would deny a method the checkout accepts.
   *
   * The setting keys are not uniform: shipping namespaces its own, the other two do not.
   */
  hasCalendar(method) {
    const config = window.ZapietCachedSettings?.cached_config;

    if (!config) {
      return true;
    }

    const key = method === 'shipping' ? 'shipping.date_picker_enabled' : `${method}_date_picker_enabled`;

    return config[key] !== false;
  }

  async fetchCalendar(method) {
    if (!this.hasCalendar(method)) {
      return { state: 'available' };
    }

    try {
      const data = await this.request(`${method}/calendar`, {
        shoppingCart: [
          {
            variant_id: this.product.variant_id,
            product_id: this.product.product_id,
            quantity: 1,
            tags: this.product.tags,
            vendors: this.product.vendors,
            product_types: this.product.product_types,
            has_components: this.product.has_components
          }
        ]
      });

      // The endpoint answers 200 with an `error` key for the expected "nothing is open" cases
      // (DATES_NOT_AVAILABLE, METHOD_NOT_ENABLED), so the status code alone is not enough.
      if (data.error) {
        return { state: 'unavailable' };
      }

      const date = firstAvailableDate(data, data.disabledDates);

      return date
        ? { state: 'available', date, timezone: data.timezone }
        : { state: 'unavailable' };
    } catch (error) {
      console.warn(`[delivery-options] ${method} calendar lookup failed`, error);
      return { state: 'unknown' };
    }
  }

  render(result) {
    this.rows.forEach((row, method) => {
      const outcome = result[method];

      if (!outcome) {
        this.setRowState(row, 'unknown');
        row.querySelector('[data-info]')?.setAttribute('hidden', '');
        return;
      }

      this.setRowState(row, outcome.state, this.statusText(row, outcome));

      const info = row.querySelector('[data-info]');

      if (info) {
        info.hidden = outcome.state !== 'unavailable' || Boolean(outcome.soldOut);
      }

      const storeButton = row.querySelector('[data-store-modal]');

      if (storeButton) {
        storeButton.hidden = true;

        if (outcome.state === 'available') {
          this.whenStorePickerReady().then((ready) => {
            storeButton.hidden = !ready;
          });
        }
      }
    });
  }

  statusText(row, outcome) {
    if (outcome.state !== 'available') {
      return row.dataset.unavailableText;
    }

    // A pre-order date pushes back whatever Zapiet offers, including "no date" for a method without a calendar.
    const date = this.minDate && (!outcome.date || outcome.date < this.minDate) ? this.minDate : outcome.date;
    const label = this.dateLabel(row, date, outcome.timezone);

    return [row.dataset.availableText, label, row.dataset.locationsText].filter(Boolean).join(' ');
  }

  /**
   * "Today" is evaluated in the store's timezone, not the visitor's: a customer browsing from
   * Vancouver at 4pm is already on the next day in Zurich, and the dates Zapiet returns are local
   * to the fulfilling location.
   */
  dateLabel(row, date, timezone) {
    if (!date) {
      return '';
    }

    const today = todayIn(timezone);

    if (date <= today) {
      return row.dataset.todayText;
    }

    if (date === addDays(today, 1)) {
      return row.dataset.tomorrowText;
    }

    const [year, month, day] = date.split('-').map(Number);
    const formatted = new Intl.DateTimeFormat(this.locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC'
    }).format(new Date(Date.UTC(year, month - 1, day)));

    return (row.dataset.fromDateText || '').replace('[date]', formatted);
  }

  setRowState(row, state, text = '') {
    const status = row.querySelector('[data-status]');

    if (!status) {
      return;
    }

    status.dataset.state = state;
    this.fillText(status.querySelector('[data-status-text]'), text, row.dataset.locationsUrl);
  }

  /**
   * Writes the status line, turning a bracketed part of the wording into a link when the row has
   * one (the pickup row and its store list). Without a link the brackets are simply dropped.
   */
  fillText(element, text, url) {
    element.replaceChildren();

    text.split(/\[([^\]]+)\]/).forEach((part, index) => {
      if (index % 2 === 0) {
        element.append(part);
      } else if (url) {
        const link = document.createElement('a');

        link.href = url;
        link.className = 'link';
        link.textContent = part;
        element.append(link);
      } else {
        element.append(part);
      }
    });
  }
}

if (!customElements.get('delivery-options')) {
  customElements.define('delivery-options', DeliveryOptions);
}
